import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../api";
import { EyeIcon, EyeOffIcon } from "../components/icons";

// Lands here from the email reset link:
//   /reset-password?token=<raw>               — bureau member (default)
//   /reset-password?token=<raw>&type=seeker   — profile seeker
// Reads the type to choose the endpoint + the login destination after a
// successful reset. The bureau side enforces 8-char min; the seeker side
// historically uses 6 (matching publicAuthController.registerPublicUser).
function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const isSeeker = params.get("type") === "seeker";
  const endpoint = isSeeker
    ? "/public/auth/reset-password"
    : "/auth/reset-password";
  const loginPath = isSeeker ? "/login" : "/bureau";
  const minLength = isSeeker ? 6 : 8;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  // Independent visibility per field — toggling one shouldn't leak the
  // other. Each input gets its own eye button.
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing or invalid reset link.");
      return;
    }
    if (password.length < minLength) {
      setError(`Password must be at least ${minLength} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await apiFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      setDone(true);
    } catch (err) {
      setError(err.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-background)] px-4">
      <div className="w-full max-w-md rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-md">
        <h1 className="text-xl font-bold text-[var(--color-text-strong)] mb-1">
          Reset password
        </h1>
        <p className="text-sm text-[var(--color-text-muted)] mb-4">
          Set a new password for your bureau account.
        </p>

        {!token && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)] mb-3">
            This reset link is missing its token. Open the link from your
            email exactly as it was sent, or request a new one.
          </div>
        )}

        {done ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-3 text-sm text-[var(--color-text)]">
              Password updated successfully. You can now sign in with your
              new password.
            </div>
            <button
              type="button"
              onClick={() => navigate(loginPath)}
              className="w-full rounded-lg bg-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-on-primary)] hover:bg-[var(--color-primary-hover)]"
            >
              Go to login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-1">
                New password
              </label>
              <div className="relative">
                <input
                  type={showNew ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`At least ${minLength} characters`}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 pr-10 text-sm"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowNew((s) => !s)}
                  aria-label={showNew ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] transition"
                >
                  {showNew ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-1">
                Confirm new password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter the password"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  className="w-full rounded-lg border border-[var(--color-border)] px-3 py-2 pr-10 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] transition"
                >
                  {showConfirm ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            {error && (
              <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full rounded-lg bg-[var(--color-primary)] py-2 text-sm font-semibold text-[var(--color-on-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-60"
            >
              {loading ? "Resetting…" : "Reset password"}
            </button>
            <button
              type="button"
              onClick={() => navigate(loginPath)}
              className="w-full text-center text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)]"
            >
              Back to login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
